/**
 * BlackoutDateEditor Component
 *
 * Dialog-based editor for creating and updating blackout dates.
 * Supports franchise-specific and company-wide blackout configuration.
 *
 * Features:
 * - Form validation with Zod schema
 * - Date range validation (end >= start)
 * - Conflict detection with warning display
 * - Franchise and service area filtering
 * - Active/inactive toggle
 *
 * @see /src/features/scheduler/hooks/useBlackoutDates.ts
 * @see /src/features/scheduler/utils/schedulerValidation.ts
 */

import React, { useState, useEffect, useMemo } from 'react'
import { AlertTriangle, Calendar } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { DatePicker } from '@/components/ui/date-picker'
import { useToast } from '@/components/ui/use-toast'
import {
  useCreateBlackoutDate,
  useUpdateBlackoutDate,
  useBlackoutDates,
} from '../hooks/useBlackoutDates'
import { useServiceAreas } from '../hooks/useServiceAreas'
import { blackoutDateSchema } from '../utils/schedulerValidation'
import { formatDateForInput, getFranchiseLabel } from '../utils/schedulerFormatters'
import type { BlackoutDate, FranchiseRegion, BlackoutDateFormData } from '../types'

interface BlackoutDateEditorProps {
  blackoutDate?: BlackoutDate
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

export function BlackoutDateEditor({
  blackoutDate,
  isOpen,
  onClose,
  onSuccess,
}: BlackoutDateEditorProps) {
  const { toast } = useToast()
  const isEditing = Boolean(blackoutDate)

  // Form state
  const [label, setLabel] = useState('')
  const [startDate, setStartDate] = useState<Date | undefined>()
  const [endDate, setEndDate] = useState<Date | undefined>()
  const [franchiseRegion, setFranchiseRegion] = useState<FranchiseRegion | 'all'>('all')
  const [serviceAreaId, setServiceAreaId] = useState<string | 'all'>('all')
  const [reason, setReason] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Mutations
  const createMutation = useCreateBlackoutDate()
  const updateMutation = useUpdateBlackoutDate()

  // Query service areas based on selected franchise
  const { data: serviceAreasData } = useServiceAreas(
    franchiseRegion !== 'all' ? { franchise_region: franchiseRegion, is_active: true } : {}
  )

  // Query for conflict detection
  const conflictQuery = useBlackoutDates(
    startDate && endDate
      ? {
          date_from: formatDateForInput(startDate),
          date_to: formatDateForInput(endDate),
          franchise_region: franchiseRegion === 'all' ? undefined : franchiseRegion,
          is_active: true,
        }
      : {}
  )

  // Calculate conflicts (excluding current blackout being edited)
  const conflicts = useMemo(() => {
    if (!conflictQuery.data?.dates) return []
    return conflictQuery.data.dates.filter((d) => d.id !== blackoutDate?.id)
  }, [conflictQuery.data?.dates, blackoutDate?.id])

  // Initialize form when editing
  useEffect(() => {
    if (blackoutDate && isOpen) {
      setLabel(blackoutDate.label)
      setStartDate(new Date(blackoutDate.start_date))
      setEndDate(new Date(blackoutDate.end_date))
      setFranchiseRegion(blackoutDate.franchise_region || 'all')
      setServiceAreaId(blackoutDate.service_area_id || 'all')
      setReason(blackoutDate.reason || '')
      setIsActive(blackoutDate.is_active)
      setErrors({})
    } else if (!isOpen) {
      // Reset form when dialog closes
      resetForm()
    }
  }, [blackoutDate, isOpen])

  // Reset service area when franchise changes
  useEffect(() => {
    if (franchiseRegion !== 'all' && !isEditing) {
      setServiceAreaId('all')
    }
  }, [franchiseRegion, isEditing])

  const resetForm = () => {
    setLabel('')
    setStartDate(undefined)
    setEndDate(undefined)
    setFranchiseRegion('all')
    setServiceAreaId('all')
    setReason('')
    setIsActive(true)
    setErrors({})
  }

  const validateForm = (): boolean => {
    try {
      const formData: BlackoutDateFormData = {
        label,
        start_date: startDate ? formatDateForInput(startDate) : '',
        end_date: endDate ? formatDateForInput(endDate) : '',
        franchise_region: franchiseRegion === 'all' ? null : franchiseRegion,
        service_area_id: serviceAreaId === 'all' ? null : serviceAreaId,
        reason: reason.trim() || null,
        is_active: isActive,
      }

      blackoutDateSchema.parse(formData)
      setErrors({})
      return true
    } catch (error: unknown) {
      const newErrors: Record<string, string> = {}
      if (error && typeof error === 'object' && 'errors' in error) {
        const zodError = error as { errors: Array<{ path: Array<string | number>; message: string }> }
        zodError.errors.forEach((err) => {
          newErrors[err.path[0]] = err.message
        })
      }
      setErrors(newErrors)
      return false
    }
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast({
        title: 'Validation error',
        description: 'Please fix the errors in the form.',
        variant: 'destructive',
      })
      return
    }

    const formData: BlackoutDateFormData = {
      label,
      start_date: formatDateForInput(startDate!),
      end_date: formatDateForInput(endDate!),
      franchise_region: franchiseRegion === 'all' ? null : franchiseRegion,
      service_area_id: serviceAreaId === 'all' ? null : serviceAreaId,
      reason: reason.trim() || null,
      is_active: isActive,
      }
  
      if (isEditing) {
        if (!blackoutDate) {
          toast({
            title: 'Missing blackout date',
            description: 'Unable to update: no blackout date was provided.',
            variant: 'destructive',
          })
          return
        }
        updateMutation.mutate(
          { id: blackoutDate.id, data: formData },
          {
            onSuccess: () => {
            onSuccess()
          },
          onError: (error) => {
            toast({
              title: 'Error updating blackout date',
              description: error.message,
              variant: 'destructive',
            })
          },
        }
      )
    } else {
      createMutation.mutate(formData, {
        onSuccess: () => {
          onSuccess()
        },
        onError: (error) => {
          toast({
            title: 'Error creating blackout date',
            description: error.message,
            variant: 'destructive',
          })
        },
      })
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Blackout Date' : 'Add Blackout Date'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the blackout date configuration.'
              : 'Create a new blackout date for scheduling restrictions.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Label */}
          <div className="space-y-2">
            <Label htmlFor="label">
              Label <span className="text-destructive">*</span>
            </Label>
            <Input
              id="label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g., Christmas Holiday, Summer Break"
              className={errors.label ? 'border-destructive' : ''}
            />
            {errors.label && <p className="text-sm text-destructive">{errors.label}</p>}
          </div>

          {/* Date Range */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>
                Start Date <span className="text-destructive">*</span>
              </Label>
              <DatePicker
                date={startDate}
                onDateChange={setStartDate}
                placeholder="Select start date"
                className={errors.start_date ? 'border-destructive' : ''}
              />
              {errors.start_date && <p className="text-sm text-destructive">{errors.start_date}</p>}
            </div>

            <div className="space-y-2">
              <Label>
                End Date <span className="text-destructive">*</span>
              </Label>
              <DatePicker
                date={endDate}
                onDateChange={setEndDate}
                placeholder="Select end date"
                disabled={(date) => (startDate ? date < startDate : false)}
                className={errors.end_date ? 'border-destructive' : ''}
              />
              {errors.end_date && <p className="text-sm text-destructive">{errors.end_date}</p>}
            </div>
          </div>

          {/* Franchise Selection */}
          <div className="space-y-2">
            <Label htmlFor="franchise">Franchise</Label>
            <Select
              value={franchiseRegion}
              onValueChange={(v) => setFranchiseRegion(v as FranchiseRegion | 'all')}
            >
              <SelectTrigger id="franchise">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Franchises (Company-wide)</SelectItem>
                <SelectItem value="hamilton">Hamilton</SelectItem>
                <SelectItem value="northyork">North York</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Select which franchise this blackout applies to, or choose "All Franchises" for
              company-wide blackouts.
            </p>
          </div>

          {/* Service Area Selection (only if franchise selected) */}
          {franchiseRegion !== 'all' && (
            <div className="space-y-2">
              <Label htmlFor="serviceArea">Service Area (Optional)</Label>
              <Select value={serviceAreaId} onValueChange={setServiceAreaId}>
                <SelectTrigger id="serviceArea">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Areas in {getFranchiseLabel(franchiseRegion)}</SelectItem>
                  {serviceAreasData?.areas.map((area) => (
                    <SelectItem key={area.id} value={area.id}>
                      {area.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                Optionally restrict this blackout to a specific service area within the franchise.
              </p>
            </div>
          )}

          {/* Reason */}
          <div className="space-y-2">
            <Label htmlFor="reason">Reason (Optional)</Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Holiday closure, Staff training, Weather-related"
              rows={3}
            />
          </div>

          {/* Active Toggle */}
          <div className="flex items-center space-x-2">
            <Checkbox id="isActive" checked={isActive} onCheckedChange={(c) => setIsActive(!!c)} />
            <Label htmlFor="isActive" className="cursor-pointer font-normal">
              Active (this blackout is currently in effect)
            </Label>
          </div>

          {/* Conflict Warning */}
          {conflicts.length > 0 && (
            <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-medium text-orange-900">Overlapping Blackouts Detected</h4>
                  <p className="text-sm text-orange-800 mt-1">
                    This date range overlaps with {conflicts.length} existing blackout
                    {conflicts.length > 1 ? 's' : ''}:
                  </p>
                  <ul className="mt-2 space-y-1">
                    {conflicts.slice(0, 3).map((conflict) => (
                      <li key={conflict.id} className="text-sm text-orange-800">
                        <Badge variant="outline" className="mr-2">
                          {conflict.label}
                        </Badge>
                        {conflict.start_date} to {conflict.end_date}
                      </li>
                    ))}
                    {conflicts.length > 3 && (
                      <li className="text-sm text-orange-800">
                        ...and {conflicts.length - 3} more
                      </li>
                    )}
                  </ul>
                  <p className="text-sm text-orange-700 mt-2">
                    You can still save this blackout, but be aware of potential scheduling conflicts.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending ? (
              <>
                <Calendar className="mr-2 h-4 w-4 animate-spin" />
                {isEditing ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>{isEditing ? 'Update' : 'Create'} Blackout Date</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
